import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const TILE_SIZE = 256;
// Approximate geographic bounds of Ghana for the default map view
const GHANA_BOUNDS = {
  north: 11.175,
  south: 4.736,
  west: -3.255,
  east: 1.199,
};
const DEFAULT_CENTER = {
  latitude: (GHANA_BOUNDS.north + GHANA_BOUNDS.south) / 2,
  longitude: (GHANA_BOUNDS.west + GHANA_BOUNDS.east) / 2,
};
const DEFAULT_ZOOM = 6.4;
const MIN_ZOOM = 3;
const MAX_ZOOM = 18;
const DEFAULT_MAP_SIZE = { width: 1100, height: 640 };

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function coordinateHasValue(value) {
  return String(value?.latitude || "").trim() !== "" && String(value?.longitude || "").trim() !== "";
}

function normalizeCoordinate(value) {
  // Empty strings become Number("") === 0, which incorrectly maps to the ocean.
  if (!coordinateHasValue(value)) {
    return DEFAULT_CENTER;
  }

  const latitude = Number(value?.latitude);
  const longitude = Number(value?.longitude);

  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return {
      latitude: clamp(latitude, -85.0511, 85.0511),
      longitude: clamp(longitude, -180, 180),
    };
  }

  return DEFAULT_CENTER;
}

function latitudeToMercatorY(latitude) {
  const sinLatitude = Math.sin((latitude * Math.PI) / 180);
  return (
    0.5 -
    Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)
  );
}

function getZoomForBounds(bounds, mapWidth, mapHeight, padding = 48) {
  const usableWidth = Math.max(mapWidth - padding * 2, 120);
  const usableHeight = Math.max(mapHeight - padding * 2, 120);

  const longitudeSpan = Math.max(bounds.east - bounds.west, 0.01);
  const mercatorSpan = Math.max(
    latitudeToMercatorY(bounds.south) - latitudeToMercatorY(bounds.north),
    0.0001,
  );

  const zoomX = Math.log2(usableWidth / (TILE_SIZE * (longitudeSpan / 360)));
  const zoomY = Math.log2(usableHeight / (TILE_SIZE * mercatorSpan));

  return clamp(Math.min(zoomX, zoomY), MIN_ZOOM, 8.5);
}

function getGhanaDefaultView(mapWidth, mapHeight) {
  return {
    center: DEFAULT_CENTER,
    zoom: getZoomForBounds(GHANA_BOUNDS, mapWidth, mapHeight),
  };
}

function coordinateToWorldPixel({ latitude, longitude }, zoom) {
  const sinLatitude = Math.sin((latitude * Math.PI) / 180);
  const scale = TILE_SIZE * 2 ** zoom;

  return {
    x: ((longitude + 180) / 360) * scale,
    y:
      (0.5 -
        Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) *
      scale,
  };
}

function worldPixelToCoordinate({ x, y }, zoom) {
  const scale = TILE_SIZE * 2 ** zoom;
  const longitude = (x / scale) * 360 - 180;
  const latitudeRadians = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / scale)));

  return {
    latitude: clamp((latitudeRadians * 180) / Math.PI, -85.0511, 85.0511),
    longitude: clamp(longitude, -180, 180),
  };
}

function formatCoordinateNumber(value) {
  return Number(value).toFixed(6);
}

export default function CoordinateMapPicker({
  value,
  onChange,
  disabled = false,
  label = "Location",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [center, setCenter] = useState(() => normalizeCoordinate(value));
  const [draftCoordinate, setDraftCoordinate] = useState(() =>
    coordinateHasValue(value) ? normalizeCoordinate(value) : null,
  );
  const [mapSize, setMapSize] = useState(DEFAULT_MAP_SIZE);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const mapRef = useRef(null);
  const dragStateRef = useRef(null);
  const hasFittedGhanaRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasFittedGhanaRef.current = false;
      return;
    }

    if (coordinateHasValue(value)) {
      const nextCenter = normalizeCoordinate(value);
      setCenter(nextCenter);
      setDraftCoordinate(nextCenter);
      setZoom((currentZoom) => Math.max(currentZoom, 12));
      hasFittedGhanaRef.current = true;
      return;
    }

    setDraftCoordinate(null);
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return undefined;

    const mapElement = mapRef.current;
    const updateMapSize = () => {
      const rect = mapElement.getBoundingClientRect();
      const nextSize = {
        width: Math.max(rect.width, 640),
        height: Math.max(rect.height, 480),
      };
      setMapSize(nextSize);

      // Fit Ghana once when opening with no selected coordinates
      if (!coordinateHasValue(value) && !hasFittedGhanaRef.current) {
        const ghanaView = getGhanaDefaultView(nextSize.width, nextSize.height);
        setCenter(ghanaView.center);
        setZoom(ghanaView.zoom);
        hasFittedGhanaRef.current = true;
      }
    };

    updateMapSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateMapSize);
      return () => window.removeEventListener("resize", updateMapSize);
    }

    const resizeObserver = new ResizeObserver(updateMapSize);
    resizeObserver.observe(mapElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, value]);

  const tileLayout = useMemo(() => {
    const mapWidth = mapSize.width;
    const mapHeight = mapSize.height;
    const tileZoom = clamp(Math.floor(zoom), MIN_ZOOM, MAX_ZOOM);
    const displayTileSize = TILE_SIZE * 2 ** (zoom - tileZoom);
    const centerPixel = coordinateToWorldPixel(center, zoom);
    const startX = centerPixel.x - mapWidth / 2;
    const startY = centerPixel.y - mapHeight / 2;
    const minTileX = Math.floor(startX / displayTileSize) - 1;
    const maxTileX = Math.floor((startX + mapWidth) / displayTileSize) + 1;
    const minTileY = Math.floor(startY / displayTileSize) - 1;
    const maxTileY = Math.floor((startY + mapHeight) / displayTileSize) + 1;
    const tileCount = 2 ** tileZoom;
    const tiles = [];

    for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
      for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
        if (tileY < 0 || tileY >= tileCount) continue;

        const wrappedTileX = ((tileX % tileCount) + tileCount) % tileCount;
        tiles.push({
          key: `${tileZoom}:${tileX}:${tileY}`,
          src: `https://tile.openstreetmap.org/${tileZoom}/${wrappedTileX}/${tileY}.png`,
          left: tileX * displayTileSize - startX,
          top: tileY * displayTileSize - startY,
          size: displayTileSize,
        });
      }
    }

    const marker = draftCoordinate
      ? {
          left: coordinateToWorldPixel(draftCoordinate, zoom).x - startX,
          top: coordinateToWorldPixel(draftCoordinate, zoom).y - startY,
        }
      : null;

    return { tiles, marker, centerPixel, mapWidth, mapHeight };
  }, [center, draftCoordinate, mapSize.height, mapSize.width, zoom]);

  const getCoordinateFromPointer = (event) => {
    const mapElement = mapRef.current;
    if (!mapElement) return null;

    const rect = mapElement.getBoundingClientRect();
    const centerPixel = coordinateToWorldPixel(center, zoom);
    const x = centerPixel.x - rect.width / 2 + (event.clientX - rect.left);
    const y = centerPixel.y - rect.height / 2 + (event.clientY - rect.top);

    return worldPixelToCoordinate({ x, y }, zoom);
  };

  const handlePointerDown = (event) => {
    if (disabled) return;

    const mapElement = mapRef.current;
    if (!mapElement) return;

    mapElement.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startCenterPixel: coordinateToWorldPixel(center, zoom),
      moved: false,
    };
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
      dragState.moved = true;
    }

    if (!dragState.moved) return;

    setCenter(
      worldPixelToCoordinate(
        {
          x: dragState.startCenterPixel.x - deltaX,
          y: dragState.startCenterPixel.y - deltaY,
        },
        zoom,
      ),
    );
  };

  const handlePointerUp = (event) => {
    const dragState = dragStateRef.current;
    dragStateRef.current = null;
    setIsDragging(false);

    if (!dragState || dragState.pointerId !== event.pointerId) return;

    mapRef.current?.releasePointerCapture?.(event.pointerId);

    if (dragState.moved) return;

    const selectedCoordinate = getCoordinateFromPointer(event);
    if (selectedCoordinate) {
      setDraftCoordinate(selectedCoordinate);
    }
  };

  const handleWheel = (event) => {
    event.preventDefault();
    if (disabled) return;

    const mapElement = mapRef.current;
    if (!mapElement) return;

    const deltaZoom = clamp(-event.deltaY * 0.003, -0.35, 0.35);
    const nextZoom = clamp(zoom + deltaZoom, MIN_ZOOM, MAX_ZOOM);
    if (nextZoom === zoom) return;

    const rect = mapElement.getBoundingClientRect();
    const pointerOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const currentCenterPixel = coordinateToWorldPixel(center, zoom);
    const pointerWorldPixel = {
      x: currentCenterPixel.x - rect.width / 2 + pointerOffset.x,
      y: currentCenterPixel.y - rect.height / 2 + pointerOffset.y,
    };
    const pointerCoordinate = worldPixelToCoordinate(pointerWorldPixel, zoom);
    const nextPointerWorldPixel = coordinateToWorldPixel(pointerCoordinate, nextZoom);
    const nextCenterPixel = {
      x: nextPointerWorldPixel.x - pointerOffset.x + rect.width / 2,
      y: nextPointerWorldPixel.y - pointerOffset.y + rect.height / 2,
    };

    setZoom(nextZoom);
    setCenter(worldPixelToCoordinate(nextCenterPixel, nextZoom));
  };

  const handleSearchLocation = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    const query = searchQuery.trim();
    if (!query) {
      setSearchError("Enter a location to search.");
      return;
    }

    setIsSearching(true);
    setSearchError("");

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=gh&q=${encodeURIComponent(query)}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Location search failed.");
      }

      const results = await response.json();
      const match = Array.isArray(results) ? results[0] : null;
      const latitude = Number(match?.lat);
      const longitude = Number(match?.lon);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setSearchError("No matching location was found.");
        return;
      }

      const nextCoordinate = normalizeCoordinate({ latitude, longitude });
      setCenter(nextCoordinate);
      setDraftCoordinate(nextCoordinate);
      setZoom((currentZoom) => Math.max(currentZoom, 14));
    } catch (error) {
      setSearchError("Unable to search for that location right now.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseLocation = () => {
    if (!draftCoordinate) return;

    onChange?.({
      latitude: formatCoordinateNumber(draftCoordinate.latitude),
      longitude: formatCoordinateNumber(draftCoordinate.longitude),
    });
    setIsOpen(false);
  };

  const modal = isOpen ? (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/55 p-2 backdrop-blur-sm sm:p-4">
      <div className="flex max-h-[96dvh] w-full max-w-7xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">Select location</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {coordinateHasValue(value)
                ? label
                : "Default view: Ghana — click the map to choose a point"}
            </p>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
            <Icon name="X" size={18} />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1">
              <label className="sr-only" htmlFor="coordinate-location-search">
                Search location
              </label>
              <input
                id="coordinate-location-search"
                type="search"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  if (searchError) setSearchError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSearchLocation(event);
                  }
                }}
                placeholder="Search town, landmark, or address"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm transition-[border-color,box-shadow] placeholder:text-muted-foreground focus:border-[#16325a] focus:outline-none focus:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
              />
              {searchError && <p className="mt-1 text-sm text-destructive">{searchError}</p>}
            </div>
            <Button type="button" variant="outline" disabled={isSearching} className="sm:w-auto" onClick={handleSearchLocation}>
              <Icon name={isSearching ? "Loader2" : "Search"} size={16} className={isSearching ? "animate-spin" : ""} />
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </div>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              {draftCoordinate
                ? `Latitude ${formatCoordinateNumber(draftCoordinate.latitude)}, Longitude ${formatCoordinateNumber(draftCoordinate.longitude)}`
                : "Click the map to choose a point."}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setZoom((currentZoom) => clamp(currentZoom - 1, MIN_ZOOM, MAX_ZOOM))}
              >
                <Icon name="Minus" size={16} />
              </Button>
              <span className="min-w-10 text-center text-sm font-medium text-foreground">
                {Number.isInteger(zoom) ? zoom : zoom.toFixed(1)}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setZoom((currentZoom) => clamp(currentZoom + 1, MIN_ZOOM, MAX_ZOOM))}
              >
                <Icon name="Plus" size={16} />
              </Button>
            </div>
          </div>

          <div
            ref={mapRef}
            role="button"
            tabIndex={0}
            className={`relative min-h-[520px] h-[min(72vh,720px)] w-full flex-1 overflow-hidden rounded-lg border border-border bg-muted touch-none ${
              isDragging ? "cursor-grabbing" : "cursor-grab"
            }`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => {
              dragStateRef.current = null;
              setIsDragging(false);
            }}
            onWheel={handleWheel}
          >
            {tileLayout.tiles.map((tile) => (
              <img
                key={tile.key}
                src={tile.src}
                alt=""
                draggable="false"
                className="absolute select-none"
                style={{
                  left: tile.left,
                  top: tile.top,
                  width: tile.size,
                  height: tile.size,
                }}
              />
            ))}
            {tileLayout.marker && (
              <div
                className="pointer-events-none absolute flex -translate-x-1/2 -translate-y-full flex-col items-center"
                style={{ left: tileLayout.marker.left, top: tileLayout.marker.top }}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#16325a] text-white shadow-lg ring-4 ring-white/90">
                  <Icon name="MapPin" size={20} />
                </div>
                <div className="h-2 w-2 rounded-full bg-[#16325a]" />
              </div>
            )}
            <div className="absolute bottom-2 right-2 rounded bg-white/90 px-2 py-1 text-[11px] text-slate-700 shadow">
              OpenStreetMap
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-border px-4 py-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleUseLocation} disabled={!draftCoordinate}>
            Use this location
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="space-y-3">
        {!coordinateHasValue(value) && (
          <div className="overflow-hidden rounded-lg border border-border bg-muted">
            <div className="border-b border-border bg-background px-3 py-2 text-sm font-medium text-foreground">
              Ghana map (default)
            </div>
            <iframe
              title="Ghana map preview"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${GHANA_BOUNDS.west}%2C${GHANA_BOUNDS.south}%2C${GHANA_BOUNDS.east}%2C${GHANA_BOUNDS.north}&layer=mapnik`}
              className="h-56 w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Open the map picker to select an exact latitude and longitude within Ghana.
            </p>
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          disabled={disabled}
          className="w-full sm:w-auto"
        >
          <Icon name="MapPin" size={16} />
          Select on map
        </Button>
      </div>
      {modal ? createPortal(modal, document.body) : null}
    </>
  );
}
